import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  addSelfNote,
  createReviewer,
  emailReviewLink,
  getAuditionReviews,
  getAuditionSummary,
  getPlan,
  getRecord,
  getRecordTypeSchema,
  getRecordWithLinks,
  listAllRecordsWithLinks,
  listAuditionTapes,
  listReviewers,
  parseReviewMarkers,
  planHas,
  revokeReviewer,
  shareablePortalUrl,
  transitionRecord,
  uploadTape,
  type AuditionReview,
  type AuditionSummaryApi,
  type AuditionTape,
  type CreatedReviewer,
  type PlanApi,
  type RecordRow,
  type RecordTypeSchema,
  type ReviewerLink,
  type ReviewerRole,
} from '../lib/api';
import { useRecordDetail } from '../lib/useRecordDetail';
import { openRecordEditor, useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { formatFieldValue, humanizeValue } from '../lib/fieldDisplay';
import { RecordVideo } from '../components/RecordMedia';
import { ReviewAudio } from '../components/ReviewAudio';
import { routePath } from '../routes/route';
import {
  AA,
  LINK,
  ageWords,
  auditionAge,
  colourOrderFrom,
  currentSides,
  ordinalWord,
  otherEnd,
  otherEnds,
  personStanding,
  seasonEpisode,
  sidesLabel,
  sidesStanding,
  sidesTrail,
  stepsFor,
  type PersonStanding,
} from '../lib/auditionSpread';
import { comingUpDayLabel, dayKeyOf } from '../lib/calendarItems';
import './spread.css';
import './selfTape.css';

// A4 — THE AUDITION, AS A SPREAD.
//
// The mirror is the decision, not a resemblance: `Project › Role › Audition`
// mirrors `Client › Location › Work Order` (fdf126f6), so this screen is the
// work-order spread's shape carrying the audition's facts. Four regions, one
// grid, and the hairline is the gap.
//
// WHAT SITS IN THE MONEY COLUMN'S PLACE, AND WHY IT IS SIDES.
//
// Two thirds of Rexx's open work orders are waiting on money, so money got a
// column rather than a footer. An audition is waiting on PAGES — what am I
// preparing, and is it still current — and decision 15155d2f already put the
// answer in the wireframe: sides are held on the ROLE with a single revision
// trail that both the audition and the engagement reference, "so 'what colour
// are we on' has exactly one answer".
//
// This screen therefore READS a trail it does not own. Every revision here
// belongs to the role; `audition_sides` is a LOOKUP recording which one was
// read from, never a master_detail, so the trail survives this audition being
// archived. The line that says whether those two agree is the whole reason
// the column exists.
//
// ===================================================================
// THE STEPPER SHOWS NO PER-GATE DATES, AND THAT IS DELIBERATE
// ===================================================================
// S2's rule for the work order was "the stepper shows WHEN each gate was
// passed". This pack cannot answer that: lifecycle transitions are not
// retained anywhere the browser can reach (`records` carries one updated_at;
// audit_logs has no read route). Reading updated_at as "in this state since"
// would produce a number that any edit to any field silently resets — a
// plausible wrong number on a stepper, which is worse than an absent one.
//
// The age that IS shown comes from fields the pack declares — due_at, then
// submitted_at (lib/auditionSpread.ts's auditionAge) — because for an
// audition silence is the story and that is the parent's actual question.
//
// THE STEPPER IS ALSO THE CONTROL. Tapping a legal next state transitions
// there. The gates are NOT pre-guessed client-side: `passed` requires an
// `outcome` (Family 3 of the pack's transition table) and the server is what
// enforces it. The refusal is shown in the server's own words.

export interface AuditionSpreadProps {
  tenantId: string;
  id: string;
  /** The role this audition hangs off — the middle rung of the breadcrumb. */
  onOpenRole: (roleId: string) => void;
  onOpenProject: (projectId: string) => void;
  onBackToList: () => void;
  /** A name on this read is a door to HouseholdPerson — the same door
   * ProjectSpine's roster opens (App.tsx's `peopleDetail` route). Optional
   * for the same reason ProjectSpine's is: a caller written before this
   * carries no such door, and a name without one stays plain text rather
   * than becoming a control that goes nowhere. */
  onOpenPerson?: (personId: string) => void;
}

type BodyTab = 'sessions' | 'tapes' | 'sides' | 'notes';

interface Around {
  /** ⚑ HAS THE SECOND ROUND TRIP LANDED YET.
   *
   * The audition itself arrives on one read; everything the hero and the
   * sides column are ABOUT — the part, the performer, the trail — arrives on
   * the next. Between the two, this screen used to render "performer not
   * recorded" and "No pages issued for this part yet": statements of absence,
   * asserted before it had looked. They self-heal in a blink on a desk, which
   * is exactly why the walk had to catch them — a parent on a slow phone
   * reads a sentence that is not true.
   *
   * So an absence is only ever claimed once this is true. Until then those
   * slots are silent, which is honest: we do not know yet. */
  loaded: boolean;
  role: RecordRow | null;
  project: RecordRow | null;
  performer: RecordRow | null;
  castingOffice: RecordRow | null;
  /** ⚑ B15 — HOW MANY TIMES THIS HOUSEHOLD HAS READ FOR THIS OFFICE, AND
   * WHICH ONE THIS IS (decision `deeb6266`, relocated). This used to render
   * on whichever name from the breakdown happened to also carry it — a
   * manager, once, in the walk that found it — which put the repeat count
   * next to the wrong relationship. The one that matters is with the
   * OFFICE, so it sits on `castingOffice`'s own line now. Never a
   * cross-tenant number: the auditions counted are the ones this family can
   * already see. */
  officeStanding: PersonStanding;
  sides: RecordRow[];
  sidesSchema: RecordTypeSchema | null;
  sessions: RecordRow[];
  /** WHO WAS IN THE ROOM (decision `deeb6266`) — the casting/production
   * figures the breakdown named. Never a cross-tenant number: the auditions
   * counted are the ones this family can already see. */
  people: RecordRow[];
}

const EMPTY_AROUND: Around = {
  loaded: false,
  role: null,
  project: null,
  performer: null,
  castingOffice: null,
  officeStanding: { reads: 0, ordinal: 0 },
  sides: [],
  sidesSchema: null,
  sessions: [],
  people: [],
};

/** A record we may not read is not an error on THIS screen — it is a blank.
 * The audition itself is gated; a casting office the caller cannot see must
 * not take the whole spread down with it. */
async function readOrNull(tenantId: string, id: string | null): Promise<RecordRow | null> {
  if (!id) return null;
  try {
    return await getRecord(tenantId, id);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
    throw err;
  }
}

function when(v: unknown): string | null {
  if (typeof v !== 'string' || !v.trim()) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** What a grant's state MEANS, in the four words a parent is actually asking.
 * `humanizeValue` would answer "Submitted", which names the row's column and
 * not the thing that happened to the tape. */
const REVIEWER_STATE: Record<string, string> = {
  active: 'link active',
  submitted: 'notes in',
  revoked: 'revoked',
  expired: 'expired',
};

/** What a reviewer IS, beside their name. `other` is deliberately rendered as
 * "reviewer" rather than as the picklist's own word — the same table the older
 * audition screen has carried since B7. */
const REVIEWER_ROLE_LABEL: Record<string, string> = {
  coach: 'coach',
  agent: 'agent',
  manager: 'manager',
  self: 'self',
  other: 'reviewer',
};

/** mm:ss for a note's time marker. */
function clock(t: number): string {
  const total = Math.max(0, Math.round(t));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** ⚑ EVERYTHING A COACH ACTUALLY SENT (Day 2 walk, S3.27).
 *
 * A coach's note came back and this screen showed a name, a date and the
 * written words. Her ROLE was dropped, the moments she MARKED while watching
 * were dropped, and the RECORDING was not there at all — so a parent read a
 * paragraph where a coach had left a voice note pinned to three points in the
 * take. Every one of those is already on the wire (`reviewerRole`,
 * `timeMarkers`, `audioFileUrl`) and the older audition screen has rendered
 * all three since B7; this screen never picked them up.
 *
 * ⚠ THE MARKS DO NOT SEEK HERE, and they say so by being text. The tape plays
 * on its own screen, not on this one, so a marker that looked like a control
 * and moved nothing would be worse than a marker that reads as what it is: the
 * moment the coach was talking about. */
function CoachNoteBody({ tenantId, review }: { tenantId: string; review: AuditionReview }) {
  const markers = parseReviewMarkers(review.timeMarkers);
  return (
    <>
      <span class="sp__row-sub">{review.body ?? review.transcript}</span>
      {review.audioFileUrl ? <ReviewAudio tenantId={tenantId} reviewId={review.id} eager /> : null}
      {markers.length > 0 ? (
        <span class="sp__row-marks" data-testid="coach-note-marks">
          marked {markers.map((m) => clock(m.t) + (m.label ? ` · ${m.label}` : '')).join(' · ')}
        </span>
      ) : null}
      {/* The written note and the spoken one are two different things a coach
          sent. When both exist, both are shown — a transcript standing in
          silently for words somebody also typed loses one of them. */}
      {review.body && review.transcript ? (
        <span class="sp__row-sub sp__row-transcript">{review.transcript}</span>
      ) : null}
    </>
  );
}

// ===================================================================
// SEND FOR NOTES — THE FAMILY'S SIDE OF THE COACH LOOP
// ===================================================================
// A reviewer link is minted by the FAMILY, always: the parent grants and the
// coach only ever asks (custodianship `a279d657`). So the whole act lives
// here, beside the tape it is about, and the coach's side reaches none of it.
//
// ⚑ THE TAPE RULE IS THE SERVER'S AND IS NOT COPIED HERE. `review_media` is
// master_detail with cardinality one, so a review without a tape is not a
// valid record — actorReviewShare.ts refuses the mint in its own sentence
// ("Add a tape to this audition before sending it for notes — there is
// nothing to watch yet"). Guessing that client-side would put a second copy
// of the rule in the browser to drift, and the refusal is already a reach
// rather than a wall: the control that adds a tape is a few lines up the same
// tab, so the sentence names the fix and the fix is on screen.
function SendForNotes({
  tenantId,
  auditionId,
  reviewers,
  failure,
  plan,
  onChanged,
}: {
  tenantId: string;
  auditionId: string;
  reviewers: ReviewerLink[];
  failure: ReadFailure | null;
  /** ⚠ A HINT, NEVER A GATE. It decides which sentence sits under the link;
   * the send itself is refused on the SERVER, which is the only place a tier
   * can be enforced. A null plan answers false and offers nothing — the safe
   * direction. */
  plan: PlanApi | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState<ReviewerRole>('coach');
  const [minting, setMinting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedReviewer | null>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  // ── W1's LAST MILE: the app sends the link, from the household's own
  // address (`email.outbound`). Until this shipped, a link was minted and the
  // family carried it to their own mail app — the work address they were
  // paying for sent nothing, ever.
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailNote, setEmailNote] = useState('');
  const [emailing, setEmailing] = useState(false);
  /** The SERVER's own sentence when a send is refused — kept on screen after
   * the form closes, so a rule the family met is a rule they got to read. */
  const [emailRefusal, setEmailRefusal] = useState<string | null>(null);
  /** The receipt: what went where, from which address. Never "sent
   * successfully" — a receipt names the objects. */
  const [emailReceipt, setEmailReceipt] = useState<string | null>(null);
  /** A failed revoke says so ON ITS OWN ROW. This is a privacy act: a link
   * the family believes dead must never quietly stay alive. */
  const [revokeRefusals, setRevokeRefusals] = useState<Record<string, string>>({});

  async function mint(e: Event) {
    e.preventDefault();
    if (minting) return;
    setMinting(true);
    setRefusal(null);
    setCopied(false);
    setCopyFailed(false);
    try {
      const made = await createReviewer(tenantId, auditionId, {
        reviewerName: name.trim() || undefined,
        reviewerRole: role,
      });
      setCreated(made);
      setName('');
      setOpen(false);
      onChanged();
    } catch (err) {
      setRefusal(err instanceof ApiError ? err.message : 'that link could not be created');
    } finally {
      setMinting(false);
    }
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // The link is on screen and selectable; only the shortcut failed, and
      // the line below says exactly that rather than swallowing it.
      setCopied(false);
      setCopyFailed(true);
    }
  }

  /** Send the link the family is holding, from the household's work address.
   *
   * ⚑ THE TOKEN GOES, NOT THE URL. `scoped_access_grants` stores only a hash,
   * so no server can rebuild a link for a grant it already minted — the raw
   * token exists once, in the answer this panel is still holding. Handing the
   * server the token lets it PROVE the link is live and this household's and
   * compose the address itself; handing it a URL would make the door a relay
   * that mails anything anywhere. */
  async function emailLink(e: Event) {
    e.preventDefault();
    if (emailing || !created) return;
    setEmailing(true);
    setEmailRefusal(null);
    setEmailReceipt(null);
    try {
      const sent = await emailReviewLink(tenantId, {
        token: created.token,
        to: emailTo.trim(),
        ...(emailNote.trim() ? { note: emailNote.trim() } : {}),
      });
      // ⚑ THE RECEIPT NAMES BOTH ADDRESSES. "Sent" alone would leave the one
      // fact a family actually needs — which address a casting director will
      // see, and therefore reply to — off the only screen that ever says it.
      setEmailReceipt(
        `Sent to ${sent.sentTo} from ${sent.sentFrom}.` +
          // The mail LEFT and only the record lagged. Said plainly, because
          // silence here would invite a second send and the coach would get
          // the same tape twice.
          (sent.recorded ? '' : ' It is not on the Mail screen yet — the record lagged behind the send.')
      );
      setEmailOpen(false);
      setEmailNote('');
    } catch (err) {
      setEmailRefusal(
        err instanceof ApiError ? err.message : 'that message could not be sent, and nothing went out'
      );
    } finally {
      setEmailing(false);
    }
  }

  async function revoke(grantId: string) {
    setRevokeRefusals((cur) => ({ ...cur, [grantId]: '' }));
    try {
      await revokeReviewer(tenantId, grantId);
      onChanged();
    } catch (err) {
      setRevokeRefusals((cur) => ({
        ...cur,
        [grantId]: err instanceof ApiError ? err.message : 'the server could not be reached',
      }));
    }
  }

  const shareLink = created ? shareablePortalUrl(created.token) : '';
  const sendOn = planHas(plan, 'email.outbound');

  return (
    <>
      <div class="sp__sec">
        <span>Send for notes</span>
        {reviewers.length > 0 ? <span>{reviewers.length} sent</span> : null}
      </div>

      {/* A REFUSED READ IS NOT AN EMPTY LIST. Said before the rows, so an
          absence below is never mistaken for "nobody has it". */}
      {failure ? (
        <p class="sp__craft-say" data-testid="reviewers-failure">
          {failure.headline} {failure.detail}
        </p>
      ) : null}

      {reviewers.map((r) => (
        <div class="sp__row" key={r.grantId} data-testid="reviewer-row">
          <span class="sp__row-top">
            <span class="sp__row-name">
              {r.reviewerName ?? 'A reviewer'}
              {r.reviewerRole ? ` — ${humanizeValue(r.reviewerRole)}` : ''}
            </span>
            <span class="sp__row-when">{REVIEWER_STATE[r.status] ?? r.status}</span>
          </span>
          {r.status === 'active' || r.status === 'submitted' ? (
            <span class="sp__row-sub">
              <button type="button" class="sp__inline-act" onClick={() => void revoke(r.grantId)}>
                Revoke this link
              </button>
            </span>
          ) : null}
          {revokeRefusals[r.grantId] ? (
            <span class="sp__row-sub" data-testid="revoke-refusal">
              The link is still live — {revokeRefusals[r.grantId]}
            </span>
          ) : null}
        </div>
      ))}

      {created ? (
        <div class="sp__link" data-testid="created-link">
          <p class="sp__craft-say">A private link — no account needed. Send it to your reviewer.</p>
          {/* ⚑ SPEC §6.3 — THE FAMILY IS TOLD, AT GRANT TIME, IN THE FLOW.
              A coach keeping the feedback THEY wrote is reasonable; a family
              discovering that later is not. One line, here, where the link is
              handed over — never buried in a policy page. */}
          <p class="sp__craft-say" data-testid="reviewer-keeps-notice">
            If they sign in, they keep a copy of the notes they write.
          </p>
          <div class="sp__linkrow">
            <input
              class="sp__linkbox"
              type="text"
              readOnly
              value={shareLink}
              aria-label="Private review link"
            />
            <button type="button" class="sp__act" onClick={() => void copyLink(shareLink)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {copyFailed ? <p class="sp__craft-say">Couldn’t copy — select it by hand.</p> : null}

          {/* ── THE SEND DOOR (W1's last mile) ──
              The work address was provisioned, billed for, and never sent a
              message: a family minted a link here and carried it to their own
              mail app. Beside Copy, because these are the two ways one link
              leaves — by hand, or by the address the household is paying for. */}
          {sendOn ? (
            emailOpen ? (
              <form class="sp__form" onSubmit={emailLink}>
                <label class="sp__field">
                  <span>Their email</span>
                  <input
                    type="email"
                    required
                    value={emailTo}
                    data-testid="email-link-to"
                    onInput={(e) => setEmailTo((e.target as HTMLInputElement).value)}
                  />
                </label>
                <label class="sp__field">
                  <span>A line with it</span>
                  <input
                    type="text"
                    maxLength={400}
                    value={emailNote}
                    data-testid="email-link-note"
                    onInput={(e) => setEmailNote((e.target as HTMLInputElement).value)}
                  />
                </label>
                <div class="sp__acts">
                  <button type="submit" class="sp__act" disabled={emailing} data-testid="email-link-send">
                    {emailing ? 'Sending…' : 'Send it'}
                  </button>
                  <button
                    type="button"
                    class="sp__act"
                    onClick={() => setEmailOpen(false)}
                    disabled={emailing}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <div class="sp__acts">
                <button
                  type="button"
                  class="sp__act"
                  data-testid="email-link"
                  onClick={() => setEmailOpen(true)}
                >
                  Email this link
                </button>
              </div>
            )
          ) : (
            /* The pack's line, not a refusal: a family on Free asked for
               nothing and is being told off for nothing. */
            <p class="sp__craft-say" data-testid="email-link-teaser">
              On Working, the app sends this from your own work address.
            </p>
          )}

          {/* The receipt names both addresses — which one a casting director
              will reply to is the fact nothing else on this screen says. */}
          {emailReceipt ? (
            <p class="sp__craft-say" data-testid="email-link-receipt">
              {emailReceipt}
            </p>
          ) : null}
          {/* The server's own sentence, kept after the form closes. */}
          {emailRefusal ? (
            <p class="sp__craft-say" data-testid="email-link-refusal">
              {emailRefusal}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* The server's own sentence, kept on screen after the form closes or
          reopens — a refusal that vanished with the form would be a rule the
          family never got to read. */}
      {refusal ? (
        <p class="sp__craft-say" data-testid="send-refusal">
          {refusal}
        </p>
      ) : null}

      {open ? (
        <form class="sp__form" onSubmit={mint}>
          <label class="sp__field">
            <span>Reviewer name</span>
            <input type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
          </label>
          <label class="sp__field">
            <span>Role</span>
            <select value={role} onChange={(e) => setRole((e.target as HTMLSelectElement).value as ReviewerRole)}>
              <option value="coach">Coach</option>
              <option value="agent">Agent</option>
              <option value="manager">Manager</option>
              <option value="other">Other</option>
            </select>
          </label>
          <div class="sp__acts">
            <button type="submit" class="sp__act" disabled={minting}>
              {minting ? 'Making the link…' : 'Make the link'}
            </button>
            <button type="button" class="sp__act" onClick={() => setOpen(false)} disabled={minting}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div class="sp__acts">
          <button type="button" class="sp__act" data-testid="send-for-notes" onClick={() => setOpen(true)}>
            Send this for notes
          </button>
        </div>
      )}
    </>
  );
}

export function AuditionSpread({
  tenantId,
  id,
  onOpenRole,
  onOpenProject,
  onBackToList,
  onOpenPerson,
}: AuditionSpreadProps) {
  const { schema, record, links, error, reload } = useRecordDetail(tenantId, AA.AUDITION, id);
  const [around, setAround] = useState<Around>(EMPTY_AROUND);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [tab, setTab] = useState<BodyTab>('sessions');
  const [refusal, setRefusal] = useState<string | null>(null);
  // B15 — every stage move leaves a receipt, in place, with a way back when
  // one exists; `confirmStage` is the one-line ask a `booked`/`passed` move
  // makes FIRST, because those two carry weight downstream (engagements, the
  // career record) that a stray tap should never commit on its own.
  const [confirmStage, setConfirmStage] = useState<string | null>(null);
  const [stageReceipt, setStageReceipt] = useState<{
    text: string;
    from: string;
    to: string;
    canUndo: boolean;
  } | null>(null);
  // B16 §7 — the one field `passed` requires (FAMILY 3, recordTypes.ts):
  // what the production actually said. Bound to the confirm's own text
  // input; Yes stays disabled while it is empty (a pass with no reason
  // recorded is the one thing the coach loop cannot use).
  const [passedOutcome, setPassedOutcome] = useState('');
  const recordsVersion = useRecordsVersion();

  // ── THE CRAFT LAYER (W6, `2f377423`) ──────────────────────────────────
  //
  // Three states this screen has to tell apart, and they are NOT the same:
  //   * the tier does not include the layer  -> one honest line where the
  //     summary would sit, and no composer. Never a button that always errors.
  //   * the tier includes it, nothing yet    -> the notes render alone; the
  //     summary slot says what it is waiting for, in the true short words.
  //   * a cached summary that predates a note -> shown, LABELLED, never
  //     silently passed off as current.
  //
  // ⚠ THE NOTES THEMSELVES ARE UNGATED and are read on every tier. What a
  // paid tier buys is the layer over them; keeping what a family handed over
  // is retention, and retention is never the thing sold.
  const [plan, setPlan] = useState<PlanApi | null>(null);
  const [reviews, setReviews] = useState<AuditionReview[]>([]);
  const [craft, setCraft] = useState<AuditionSummaryApi | null>(null);
  /** A read that failed for a reason that is NOT "your tier does not have
   * this". Rendered rather than swallowed — the notes are the point of this
   * region and a family must never read an empty one as "nobody wrote". */
  const [craftSay, setCraftSay] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [noteRefusal, setNoteRefusal] = useState<string | null>(null);
  const craftOn = planHas(plan, 'craft.layer');

  useEffect(() => {
    let cancelled = false;
    // A plan that cannot be read leaves `plan` null, and `planHas` answers
    // false for null — the safe direction: nothing is offered.
    void getPlan(tenantId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    async function loadNotes() {
      try {
        const rows = await getAuditionReviews(tenantId, id);
        if (cancelled) return;
        setReviews(rows);
        setCraftSay(null);
      } catch (err) {
        if (cancelled) return;
        // 403/404 here means the caller may not read this audition's notes at
        // all (a child is seated below the type's read role) — a blank, the
        // same line `readOrNull` draws for the casting office. Anything else
        // is a real failure and is said out loud.
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
          setReviews([]);
          setCraftSay(null);
        } else {
          setCraftSay('The notes on this read could not be loaded just now.');
        }
      }
      if (!craftOn) {
        if (!cancelled) setCraft(null);
        return;
      }
      try {
        const summary = await getAuditionSummary(tenantId, id);
        if (!cancelled) setCraft(summary);
      } catch {
        // The gate already said this household has the layer, so a refusal
        // here is a server-side disagreement rather than a tier: leave the
        // slot empty rather than assert an absence we did not verify.
        if (!cancelled) setCraft(null);
      }
    }
    void loadNotes();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, craftOn]);

  // ── THE TAPE, ON THE READ IT WAS MADE FOR ─────────────────────────────
  //
  // ⚑ WITHOUT THIS REGION AN AUDITION WITH A TAPE LOOKED EXACTLY LIKE ONE
  // WITHOUT. The tape is the thing the read produced and the thing a coach is
  // sent; it was reachable only from Materials, by a parent who already knew
  // it existed.
  //
  // Read on its own round trip, and settled rather than raced: a hiccup in
  // the reviewer links must not blank the tapes beside them, and each says
  // which of the two truths it is holding — nothing here, or we could not
  // read (lib/readFailure.ts's rule).
  const [tapes, setTapes] = useState<AuditionTape[]>([]);
  const [tapesLoaded, setTapesLoaded] = useState(false);
  const [tapesFailure, setTapesFailure] = useState<ReadFailure | null>(null);
  const [reviewers, setReviewers] = useState<ReviewerLink[]>([]);
  const [reviewersFailure, setReviewersFailure] = useState<ReadFailure | null>(null);
  const [feedbackNonce, setFeedbackNonce] = useState(0);
  const [attaching, setAttaching] = useState(false);
  const [attachRefusal, setAttachRefusal] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTapesLoaded(false);
    async function loadFeedback() {
      const [t, rv] = await Promise.allSettled([
        listAuditionTapes(tenantId, id),
        listReviewers(tenantId, id),
      ]);
      if (cancelled) return;
      if (t.status === 'fulfilled') {
        setTapes(t.value);
        setTapesFailure(null);
      } else setTapesFailure(describeReadFailure(t.reason));
      if (rv.status === 'fulfilled') {
        setReviewers(rv.value);
        setReviewersFailure(null);
      } else setReviewersFailure(describeReadFailure(rv.reason));
      setTapesLoaded(true);
    }
    void loadFeedback();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion, feedbackNonce]);

  /** The bytes go straight to R2 (presign → PUT → commit, lib/api.ts's
   * uploadTape), so a phone's two-gigabyte take never transits the app heap.
   * Nothing about the evening is asked for here: the tape's own record holds
   * the reader, the corner and which take went, and this door is one tap. */
  async function attachTape(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || attaching) return;
    setAttaching(true);
    setAttachRefusal(null);
    try {
      await uploadTape(tenantId, id, file);
      setFeedbackNonce((n) => n + 1);
    } catch (err) {
      setAttachRefusal(err instanceof ApiError ? err.message : 'that tape could not be added to this read');
    } finally {
      setAttaching(false);
      input.value = '';
    }
  }

  /** One tape is one door — the tape's own record, where what came back on it
   * lives. The hash is walked here rather than through a caller's prop, the
   * same as AuditionDetail does for its project. */
  function openTape(tapeId: string) {
    window.location.hash = routePath({ screen: 'tape', id: tapeId });
  }

  const coachNotes = reviews.filter((r) => r.reviewerRole !== 'self' && (r.body ?? r.transcript));
  const selfNotes = reviews.filter((r) => r.reviewerRole === 'self' && (r.body ?? r.transcript));

  async function saveSelfNote() {
    const text = draft.trim();
    if (!text || saving) return;
    setSaving(true);
    setNoteRefusal(null);
    try {
      await addSelfNote(tenantId, id, text);
      setDraft('');
      const rows = await getAuditionReviews(tenantId, id);
      setReviews(rows);
      // The summary is a view over the notes and one just changed. The server
      // refreshes a stale cache on this read, so asking again is what makes
      // the new note visible in it.
      if (craftOn) setCraft(await getAuditionSummary(tenantId, id).catch(() => null));
    } catch (err) {
      // ⚠ `no_tape_to_note` IS THE INTERESTING ONE and it is not a fault: a
      // note lives on the take it is about, and this read has no take yet.
      // The server's own words, because they are the ones a parent can act on.
      setNoteRefusal(err instanceof ApiError ? err.message : 'that note could not be saved');
    } finally {
      setSaving(false);
    }
  }

  const roleId = record ? otherEnd(links, record.id, LINK.AUDITION_ROLE) : null;
  const performerId = record ? otherEnd(links, record.id, LINK.AUDITION_SUBJECT) : null;
  const officeId = record ? otherEnd(links, record.id, LINK.AUDITION_CASTING_OFFICE) : null;
  const readSidesId = record ? otherEnd(links, record.id, LINK.AUDITION_SIDES) : null;
  // Joined into a stable string so the effect below re-runs when the SET of
  // people changes, not on every render that produces a new array.
  const peopleIds = record ? otherEnds(links, record.id, LINK.AUDITION_PEOPLE) : [];
  const peopleKey = peopleIds.join(',');

  useEffect(() => {
    if (!record) return;
    let cancelled = false;
    setAround((prev) => ({ ...prev, loaded: false }));
    async function load() {
      try {
        const [role, performer, castingOffice, sidesSchema, allSides, allSessions, peopleRecords, allAuditions] =
          await Promise.all([
            readOrNull(tenantId, roleId),
            readOrNull(tenantId, performerId),
            readOrNull(tenantId, officeId),
            getRecordTypeSchema(tenantId, AA.SIDES).catch(() => null),
            listAllRecordsWithLinks(tenantId, AA.SIDES, { linkTypes: [LINK.SIDES_ROLE] }),
            listAllRecordsWithLinks(tenantId, AA.SESSION, { linkTypes: [LINK.SESSION_AUDITION] }),
            // ⚠ aa_person reads at 'staff'. A child or a helper gets 403 on
            // each of these and the line simply does not appear for them —
            // which is the pack's own rule (an agent's phone number is not a
            // child's screen), not a failure to report. readOrNull already
            // draws exactly that line for the casting office beside it.
            Promise.all(peopleIds.map((pid) => readOrNull(tenantId, pid))),
            // THE COUNT'S ONLY SOURCE: this tenant's own auditions and their
            // `people`/`audition_casting_office` links. There is no
            // cross-household number here and there must never be one
            // (`deeb6266`). Fetched whenever EITHER the breakdown named
            // people OR there is an office to count reads for — B15 moved
            // the ordinal onto the office's own line, so the office alone
            // now needs this too.
            peopleIds.length > 0 || officeId
              ? listAllRecordsWithLinks(tenantId, AA.AUDITION, {
                  linkTypes: [LINK.AUDITION_PEOPLE, LINK.AUDITION_CASTING_OFFICE],
                })
              : Promise.resolve(null),
          ]);
        if (cancelled) return;

        const people = peopleRecords.filter((r): r is RecordRow => r !== null);
        // ⚑ B15 — THE OFFICE'S OWN READ COUNT, moved here from wherever a
        // person happened to be linked as `people` too (see `Around`'s own
        // comment on why this relocated). `inRead` (auditionSpread.ts) is
        // what makes this count the SAME reads `readsWithPerson`/
        // `personStanding` always have — either end of the room.
        const officeStanding =
          officeId && allAuditions
            ? personStanding(activeOnly(allAuditions.records), allAuditions.linksByRecordId, officeId, id)
            : { reads: 0, ordinal: 0 };

        // The trail belongs to the ROLE, so it is filtered by the role — not
        // by this audition. An audition that read from Blue still shows Pink
        // above it, which is the entire point of the column.
        const sides = roleId
          ? activeOnly(allSides.records).filter(
              (r) => otherEnd(allSides.linksByRecordId[r.id] ?? [], r.id, LINK.SIDES_ROLE) === roleId
            )
          : [];
        const sessions = activeOnly(allSessions.records).filter(
          (r) => otherEnd(allSessions.linksByRecordId[r.id] ?? [], r.id, LINK.SESSION_AUDITION) === id
        );

        // The project is one hop above the role, and the role's own link set
        // already contains it — core's listLinks matches src_id OR dst_id, so
        // a record carries everything pointing at it as well as everything it
        // points at.
        const roleLinks = role ? await getRecordWithLinks(tenantId, role.id).catch(() => null) : null;
        const project = await readOrNull(
          tenantId,
          role && roleLinks ? otherEnd(roleLinks.links, role.id, LINK.ROLE_PROJECT) : null
        );
        if (cancelled) return;

        setAround({
          loaded: true,
          role,
          project,
          performer,
          castingOffice,
          officeStanding,
          sides,
          sidesSchema,
          sessions,
          people,
        });
        setFailure(null);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, record?.id, roleId, performerId, officeId, peopleKey, recordsVersion]);

  if (error) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Audition</div>
        <div class="sp__say">
          {error.headline}
          <br />
          <span class="sp__say-soft">{error.detail}</span>
        </div>
        {error.retryable ? (
          <button type="button" class="sp__retry" onClick={reload}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  if (!record) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Audition</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const colourOrder = colourOrderFrom(around.sidesSchema);
  const trail = sidesTrail(around.sides, colourOrder);
  const standing = sidesStanding(trail, colourOrder, readSidesId);
  const head = currentSides(trail, colourOrder);
  const steps = stepsFor(schema?.lifecycle, record.lifecycle_state);
  const legalNext = schema?.lifecycle?.transitions[record.lifecycle_state ?? ''] ?? [];
  // B16 §7 — FAMILY 5's own floor: `archived` back to any active stage.
  // Prefers the stage THIS session watched it get archived FROM (this
  // move's own receipt); with no such receipt, `submitted` is always legal
  // from `archived` (every AUDITION_ACTIVE stage is), so it is never a
  // guess past what the fetched schema allows.
  const bringBackTarget = stageReceipt && stageReceipt.to === 'archived' ? stageReceipt.from : 'submitted';
  const age = auditionAge(record);
  // B15 — a 'sent' age carries the moment itself, not a day count; its
  // words are the same day formatter Today's "Coming up" uses
  // (auditionSpread.ts's own AuditionAge comment on why this file, not that
  // one, does the wording).
  const ageLine = age?.kind === 'sent' ? `Sent ${comingUpDayLabel(dayKeyOf(age.at))}` : ageWords(age);
  const modality = record.fields.modality;
  const se = seasonEpisode(record.fields);
  /** The audition's OWN `notes` field — a different thing from the notes
   * people wrote about the take, which are `aa_review` rows. Both live under
   * the Notes tab; neither stands in for the other. */
  const auditionNote =
    typeof record.fields.notes === 'string' && record.fields.notes.trim() ? record.fields.notes : null;

  /** The one that PLAYS in place: the newest tape carrying actual bytes. A
   * tape logged but never attached is still a row — it just has nothing to
   * put in a player, and an empty player over it would read as the recording
   * being lost. */
  const playable = [...tapes]
    .reverse()
    .find((t) => typeof t.fields.original_file_ref === 'string' && t.fields.original_file_ref);

  // ===================================================================
  // B15 — A STAGE MOVE, AND WHAT IT LEAVES BEHIND
  // ===================================================================
  // The walk's own finding: one tap on the "Submitted" dot moved the stage
  // silently — no receipt, no undo — and the same tap on "Booked" books a
  // job without a word. `doMove` is now the ONE path every stage change on
  // this screen takes (the stepper, the confirm's "Yes", and the "Sent it?"
  // offer below all call it), so a receipt and an undo are never a fact one
  // caller remembers to add and another forgets.
  const roleLabel = around.role ? around.role.display_name : 'This read';

  async function doMove(to: string, fields?: Record<string, unknown>, receiptText?: string) {
    setRefusal(null);
    setStageReceipt(null);
    // `record` is narrowed non-null by the render-guard above, but that
    // narrowing does not reach into this nested function declaration.
    const from = record!.lifecycle_state;
    try {
      await transitionRecord(tenantId, id, to, fields);
      // THE GENERATED EDGES, NOT A HAND-KEPT LIST. Undo is offered only when
      // the schema's own transition table already carries the move back —
      // the fetched `schema.lifecycle.transitions`, the exact map the
      // stepper's own `legalNext` reads. This file adds no second copy of
      // the pack's vocabulary to answer "can this reverse".
      const canUndo = Boolean(from && schema?.lifecycle?.transitions[to]?.includes(from));
      setStageReceipt({ text: receiptText ?? `${roleLabel} moved to ${humanizeValue(to)}.`, from: from ?? '', to, canUndo });
      reload();
    } catch (err) {
      // THE GATE IS THE SERVER'S. `passed` requires an outcome and the pack
      // says so in its own transition table; guessing that client-side would
      // put a second copy of the rule here to drift. The refusal is shown in
      // the words the server used.
      setRefusal(err instanceof ApiError ? err.message : 'that move was refused');
    }
  }

  /** What a tap on a stepper dot does. `booked` and `passed` carry weight
   * downstream (engagements, the career record) and ask first, one line, in
   * place — every other move goes straight through, exactly as it always
   * has. Moving to `submitted` stamps `submitted_at` = now: the engine's
   * `set_field` side effect only ever writes a literal value declared at
   * seed time (records/effects.ts's `SetFieldEffect` — `value` is checked
   * against the field's type, never "now"), so there is no way to have the
   * pack's own lifecycle stamp a live clock reading on a transition — the
   * client sets it in the same act instead. */
  function handleStepTap(to: string) {
    if (to === 'booked' || to === 'passed') {
      setRefusal(null);
      setStageReceipt(null);
      setConfirmStage(to);
      return;
    }
    void doMove(to, to === 'submitted' ? { submitted_at: new Date().toISOString() } : undefined);
  }

  function confirmStageMove() {
    const to = confirmStage;
    if (!to) return;
    setConfirmStage(null);
    // B16 §7 — `passed` is FAMILY 3's own required field, carried the same
    // way `submitted` already stamps `submitted_at` above: the field the
    // move needs rides with the move, in the same act.
    void doMove(to, to === 'passed' ? { outcome: passedOutcome.trim() } : undefined);
  }

  /** B16 §7 — the two quiet actions under the stepper: `passed` and
   * `archived` are legal from every active stage (FAMILIES 3/4) but are
   * OFF-SPINE, so no stepper dot for either ever renders while the read is
   * still active — B15's "Mark … passed?" confirm existed and had no door.
   * `askPassed` opens the SAME confirm the (dead) stepper tap would have,
   * extended with the outcome field FAMILY 3 requires. */
  function askPassed() {
    setRefusal(null);
    setStageReceipt(null);
    setPassedOutcome('');
    setConfirmStage('passed');
  }

  /** FAMILY 4 requires nothing — the whole point of the park is that a
   * casting office going quiet needs no explanation from the household — so
   * this goes straight through, no confirm, exactly like every non-`booked`/
   * `passed` stepper tap. */
  function putAway() {
    void doMove('archived', undefined, `${roleLabel} put away.`);
  }

  /** Moves back to the state the receipt's move came FROM. Undoing OUT of
   * `submitted` clears the stamp that move just set — the same field, the
   * same act, exactly as `doMove` sets it going forward. */
  function undoStageMove() {
    if (!stageReceipt || !stageReceipt.canUndo) return;
    const { from, to } = stageReceipt;
    setStageReceipt(null);
    const fields = to === 'submitted' && from === 'requested' ? { submitted_at: null } : undefined;
    void doMove(from, fields);
  }

  const rail: { label: string; n: number; go?: () => void; on?: boolean; always?: boolean }[] = [
    { label: 'Sessions', n: around.sessions.length, go: () => setTab('sessions'), on: tab === 'sessions' },
    // ⚑ THE PILL IS NEVER DISABLED AT ZERO, unlike its neighbours: the tab it
    // opens is where a tape gets ADDED, so a read with none is exactly the
    // read that has to be able to reach it.
    { label: 'Tapes', n: tapes.length, go: () => setTab('tapes'), on: tab === 'tapes', always: true },
    { label: 'Sides', n: trail.length, go: () => setTab('sides'), on: tab === 'sides' },
    // ⚑ THE COUNT INCLUDES THE NOTES PEOPLE WROTE, not only the audition's own
    // `notes` field. A rail pill at zero is DISABLED, so counting the field
    // alone would lock a family out of the tab their coaches' feedback now
    // lives in — the exact shape of "built onto a screen with no way in".
    {
      label: 'Notes',
      n: (auditionNote ? 1 : 0) + reviews.length,
      go: () => setTab('notes'),
      on: tab === 'notes',
    },
    {
      label: 'Casting office',
      n: around.castingOffice ? 1 : 0,
      // A real person record behind the pill makes it a door (ProjectSpine's
      // roster pattern); with no record — or no caller door — it stays a count.
      go:
        onOpenPerson && around.castingOffice
          ? () => onOpenPerson(around.castingOffice!.id)
          : undefined,
    },
    {
      label: 'People',
      n: around.people.length,
      // One person is one door; several keep their own doors in the hero line.
      go:
        onOpenPerson && around.people.length === 1
          ? () => onOpenPerson(around.people[0]!.id)
          : undefined,
    },
    // ⚑ THE LINK IS THE COUNT, NOT THE READ (Day 2 walk, S3.19). aa_subject
    // declares `min_role_read: 'staff'` and a child is seated at `member`, by
    // design — the type-level gate is the only thing standing between her and
    // her sister's union number. So on HER OWN read the performer record came
    // back refused, `around.performer` was null, and this pill said 0 while
    // the parent's said 1 about the same record. The link is right here in
    // `links` and needs no permission to count: a read that names a performer
    // says so to everyone, and only the NAME is withheld.
    { label: 'Performer', n: performerId ? 1 : 0 },
  ];

  return (
    <div class="sp">
      <div class="sp__crumb">
        {around.project ? (
          <>
            <button type="button" onClick={() => onOpenProject(around.project!.id)}>
              {around.project.display_name}
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : null}
        {around.role ? (
          <>
            <button type="button" onClick={() => onOpenRole(around.role!.id)}>
              {around.role.display_name}
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        ) : (
          <>
            <button type="button" onClick={onBackToList}>
              Auditions
            </button>
            <span class="sp__crumb-sep">›</span>
          </>
        )}
        <span class="sp__crumb-cur">this read</span>
      </div>

      <div class="sp__grid">
        {/* ---- HERO: the character is the subject, the read is the reference ---- */}
        <div class="sp__hero">
          <div class="sp__hero-top">
            <div>
              <div class="sp__label">
                {around.project ? <span>{around.project.display_name}</span> : null}
                {typeof modality === 'string' && modality ? (
                  <span class="sp__chip">{humanizeValue(modality)}</span>
                ) : null}
                {se ? <span class="sp__chip">{se}</span> : null}
              </div>
              {/* The CHARACTER is the h3. A work order's h3 is the client; the
                  audition's is the part, because that is what this record is
                  about. The audition's own id is never shown — it is a key,
                  not a fact anyone holds. */}
              {/* THE CHARACTER, OR NOTHING PRETENDING TO BE ONE.
                  aa_audition's name_template is `{modality}` (B15 — was
                  `{modality} — {submitted_at}`), so display_name is at best
                  an unhumanized modality value — correct as a record name,
                  unreadable as a headline. A read whose part is genuinely
                  unrecorded says the true short thing instead. */}
              <h3 class="sp__title">{around.role ? around.role.display_name : 'This read'}</h3>
              {/* WHOSE IT IS, IN THE HERO, ALWAYS. This is a household
                  product; an audition that does not say which child it belongs
                  to has lost the only fact that spans the app.

                  THE PARTS ARE JOINED, NEVER PREFIXED. Written as ` · x` per
                  clause, a line whose first clause is absent opens with an
                  orphan separator — "· out Aug 14". Caught from a screenshot
                  2026-08-19. */}
              <p class="sp__sub">
                {[
                  // ⚠ AND THE SENTENCE NEVER CLAIMS AN ABSENCE THE LINK
                  // DISPROVES. "performer not recorded" is only true when no
                  // `subject` link exists at all; said to a child who simply
                  // may not read the record, it is the app telling her that
                  // her own read is nobody's.
                  around.performer
                    ? around.performer.display_name
                    : around.loaded && !performerId
                      ? 'performer not recorded'
                      : null,
                  around.castingOffice ? `read for ${around.castingOffice.display_name}` : null,
                  // ⚑ B15 — THE NTH-READ BADGE, MOVED HERE. It used to render
                  // on whichever breakdown-named person happened to carry it
                  // (a manager, in the walk that found it) — the repeat
                  // relationship that matters is with the OFFICE, so it sits
                  // beside "read for {office}" now, and only from the SECOND
                  // read: "1st read" is noise dressed as information.
                  around.castingOffice && around.officeStanding.reads >= 2 && around.officeStanding.ordinal > 0
                    ? `${ordinalWord(around.officeStanding.ordinal)} read`
                    : null,
                  // B19 §4 — DROPPED once the age line already says "Sent":
                  // `ageLine` above prints "Sent {day}" from this same
                  // `submitted_at` the moment `age?.kind === 'sent'`, and an
                  // "out {day}" clause beside it is the same fact twice. Kept
                  // for every OTHER age (due/overdue/out/none) — a read whose
                  // age line reads "due in 3 days" while carrying a stray
                  // `submitted_at` from an earlier state is a real, different
                  // fact, not a duplicate.
                  age?.kind !== 'sent' && when(record.fields.submitted_at)
                    ? `out ${when(record.fields.submitted_at)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {/* WHO WAS IN THE ROOM — decision `deeb6266`. The role is
                  rendered beside each name because that is what makes the
                  line teach itself: "Dana Whitfield — Casting Director"
                  needs no caption above it saying what these names are.
                  B15 moved the repeat-read ordinal off this line and onto
                  "read for {office}" above — see that line's own comment. */}
              {around.people.length > 0 ? (
                <p class="sp__people">
                  {around.people.map((p, i) => (
                    <span key={p.id}>
                      {i > 0 ? ' · ' : ''}
                      {/* A real record behind the name makes it a door —
                          ProjectSpine's roster pattern. Without the caller's
                          door it stays plain text, never a dead control. */}
                      {onOpenPerson ? (
                        <button
                          type="button"
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                          onClick={() => onOpenPerson(p.id)}
                        >
                          {p.display_name}
                        </button>
                      ) : (
                        p.display_name
                      )}
                      {typeof p.fields.person_role === 'string' && p.fields.person_role
                        ? ` — ${humanizeValue(p.fields.person_role)}`
                        : ''}
                    </span>
                  ))}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              class="sp__edit"
              onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.AUDITION, id })}
            >
              Edit
            </button>
          </div>

          <div class="sp__steps">
            {steps.map((step) => {
              // A step is tappable only when the pack's own transition table
              // says the move is legal from where the record actually is.
              // That list comes from the fetched schema; nothing here knows a
              // state's name.
              const can = legalNext.includes(step.state);
              return (
                <div class="sp__step-cell" key={step.state}>
                  <button
                    type="button"
                    class={`sp__step${step.reached ? ' sp__step--reached' : ''}${
                      step.current ? ' sp__step--current' : ''
                    }${step.offSpine ? ' sp__step--off' : ''}`}
                    disabled={!can}
                    onClick={() => handleStepTap(step.state)}
                  >
                    <span class="sp__dot" />
                    <span class="sp__step-tx">{step.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
          {ageLine ? (
            <p class={`sp__age${age?.kind === 'overdue' ? ' sp__age--late' : ''}`}>{ageLine}</p>
          ) : null}
          {/* B16 §7 — `passed`/`archived` are legal from every active stage
              (FAMILIES 3/4) but sit OFF the spine, so no stepper dot for
              either ever renders while the read is still active — these two
              quiet actions are the only door onto them until then. Each is
              gated on the FETCHED schema's own `legalNext`, not a hand list:
              a pack that changed the families would show neither rather
              than a stale one. */}
          {!confirmStage && legalNext.includes('passed') ? (
            <button type="button" class="sp__inline-act" data-testid="stage-ask-passed" onClick={askPassed}>
              Passed
            </button>
          ) : null}
          {!confirmStage && legalNext.includes('archived') ? (
            <button type="button" class="sp__inline-act" data-testid="stage-put-away" onClick={putAway}>
              No word — put it away
            </button>
          ) : null}
          {/* B15 — `booked`/`passed` ask first, in place, one line: these two
              carry weight downstream (engagements, the career record) that a
              stray tap must never commit on its own. B16 §7 extends `passed`
              with the one field FAMILY 3 requires — a pass with no reason
              recorded is the one thing the coach loop cannot use, so Yes
              stays disabled until it is filled. */}
          {confirmStage ? (
            <p class="sp__age" data-testid="stage-confirm">
              Mark {roleLabel} {confirmStage === 'booked' ? 'booked' : 'passed'}?
              {confirmStage === 'passed' ? (
                <>
                  {' '}
                  What did they say?
                  <br />
                  <input
                    type="text"
                    class="sp__outcome-input"
                    data-testid="stage-confirm-outcome"
                    value={passedOutcome}
                    onInput={(e) => setPassedOutcome((e.target as HTMLInputElement).value)}
                  />
                  <br />
                </>
              ) : (
                ' '
              )}
              <button
                type="button"
                class="sp__inline-act"
                data-testid="stage-confirm-yes"
                disabled={confirmStage === 'passed' && passedOutcome.trim().length === 0}
                onClick={confirmStageMove}
              >
                Yes
              </button>{' '}
              <button
                type="button"
                class="sp__inline-act"
                data-testid="stage-confirm-no"
                onClick={() => setConfirmStage(null)}
              >
                Not yet
              </button>
            </p>
          ) : null}
          {/* THE RECEIPT, WITH A WAY BACK WHEN ONE EXISTS. Never an
              acknowledgement — it names the object and the move that
              happened to it. */}
          {stageReceipt ? (
            <p class="sp__age" data-testid="stage-receipt">
              {stageReceipt.text}
              {stageReceipt.canUndo ? (
                <>
                  {' '}
                  <button type="button" class="sp__inline-act" data-testid="stage-undo" onClick={undoStageMove}>
                    Undo
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {/* B17 §3 — under the "moved to Booked" receipt above: the reaction
              (`booked-offers-job`, actorAssistReactions.ts) stages the job as
              a proposal, never creates it outright — this line says where it
              landed. */}
          {stageReceipt && stageReceipt.to === 'booked' ? (
            <p class="sp__age" data-testid="stage-booked-offer">
              The job is waiting on Today — approve it to start the dates and the credit.
            </p>
          ) : null}
          {refusal ? (
            <p class="sp__age sp__age--late" data-testid="stage-refusal">
              {refusal}
            </p>
          ) : null}
          {/* B16 §7 — FAMILY 5: back OUT of `archived` to any active stage,
              which is what makes the park revivable rather than a second
              name for a pass (`passed` has no such family — it is a genuine
              terminus, and this action never shows for it). Prefers the
              stage THIS session watched it get archived FROM; with no such
              receipt (a read opened already archived), `submitted` is
              family 5's own floor — always legal from `archived` — so it is
              never a guess past what the fetched schema actually allows. */}
          {record.lifecycle_state === 'archived' && legalNext.includes(bringBackTarget) ? (
            <p class="sp__age" data-testid="stage-bring-back">
              <button
                type="button"
                class="sp__inline-act"
                data-testid="stage-bring-back-act"
                onClick={() => void doMove(bringBackTarget)}
              >
                Bring it back
              </button>
            </p>
          ) : null}
        </div>

        {/* ---- SIDES: a column, not a footer ---- */}
        <div class="sp__aside">
          <div class="sp__sec">
            <span>Sides</span>
            {around.role ? <span>on {around.role.display_name}</span> : null}
          </div>

          {head ? (
            <>
              <div class="sp__big">{sidesLabel(head)}</div>
              <p class="sp__note">
                {when(head.fields.issued_at) ? <>issued {when(head.fields.issued_at)}</> : null}
                {typeof head.fields.page_count === 'number' ? <> · {head.fields.page_count} pp</> : null}
              </p>
              {typeof head.fields.changed_scenes === 'string' && head.fields.changed_scenes.trim() ? (
                <p class="sp__note">{head.fields.changed_scenes}</p>
              ) : null}
            </>
          ) : around.loaded ? (
            <div class="sp__empty">No pages issued for this part yet.</div>
          ) : null}

          {/* THE LINE THE COLUMN EXISTS FOR. Neither agreement nor
              disagreement is left silent, and an audition pointing at no
              revision is never assumed to be on the latest. */}
          {standing.kind === 'behind' ? (
            <p class="sp__standing sp__standing--behind">
              This read was from <b>{sidesLabel(standing.read)}</b>.{' '}
              <b>{sidesLabel(standing.current)}</b> has been issued since.
            </p>
          ) : null}
          {standing.kind === 'current' ? (
            <p class="sp__standing">
              This read was from <b>{sidesLabel(standing.read)}</b> — the current pages.
            </p>
          ) : null}
          {around.loaded && standing.kind === 'unrecorded' && trail.length > 0 ? (
            <p class="sp__standing">Which revision this was read from is not recorded.</p>
          ) : null}

          {trail.length > 0 ? (
            <div class="sp__trail">
              {trail.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  class={`sp__trail-row${head && row.id === head.id ? ' sp__trail-row--head' : ''}`}
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.SIDES, id: row.id })}
                >
                  <span>{sidesLabel(row)}</span>
                  <span class="sp__trail-when">
                    {when(row.fields.issued_at) ?? '—'}
                    {typeof row.fields.page_count === 'number' ? ` · ${row.fields.page_count}pp` : ''}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <div class="sp__acts">
            {/* Recording a revision creates it against the ROLE, which is
                where the trail lives. Creating one from here and having it
                land on the audition would fork the trail the decision exists
                to keep single. */}
            {around.role ? (
              <button
                type="button"
                class="sp__act"
                onClick={() =>
                  openRecordEditor({
                    mode: 'create',
                    typeKey: AA.SIDES,
                    prefillRels: { [LINK.SIDES_ROLE]: [around.role!.id] },
                  })
                }
              >
                Record a revision
              </button>
            ) : null}
            {around.role ? (
              <button type="button" class="sp__act" onClick={() => onOpenRole(around.role!.id)}>
                Open the part
              </button>
            ) : null}
          </div>
        </div>

        {/* ---- WORK: tabs, and the first one is the story ---- */}
        <div class="sp__body">
          <div class="sp__tabs">
            <button type="button" class={`sp__tab${tab === 'sessions' ? ' sp__tab--on' : ''}`} onClick={() => setTab('sessions')}>
              Sessions
            </button>
            <button type="button" class={`sp__tab${tab === 'tapes' ? ' sp__tab--on' : ''}`} onClick={() => setTab('tapes')}>
              Tapes
            </button>
            <button type="button" class={`sp__tab${tab === 'sides' ? ' sp__tab--on' : ''}`} onClick={() => setTab('sides')}>
              Sides
            </button>
            <button type="button" class={`sp__tab${tab === 'notes' ? ' sp__tab--on' : ''}`} onClick={() => setTab('notes')}>
              Notes
            </button>
          </div>

          {tab === 'sessions' ? (
            !around.loaded ? null : around.sessions.length === 0 ? (
              <div class="sp__empty">No sessions recorded for this read.</div>
            ) : (
              around.sessions.map((s) => (
                <button
                  type="button"
                  key={s.id}
                  class="sp__row"
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.SESSION, id: s.id })}
                >
                  <span class="sp__row-top">
                    <span class="sp__row-name">{s.display_name}</span>
                    <span class="sp__row-when">{when(s.fields.scheduled_at) ?? ''}</span>
                  </span>
                  <span class="sp__row-sub">
                    {/* A SESSION'S OWN MODALITY IS AUTHORITATIVE FOR THAT
                        VISIT — the pack's own reading rule. It is rendered as
                        it is, never reconciled with the audition's. */}
                    {typeof s.fields.session_kind === 'string' ? humanizeValue(s.fields.session_kind) : null}
                    {typeof s.fields.modality === 'string' && s.fields.modality
                      ? ` · ${humanizeValue(s.fields.modality)}`
                      : ''}
                    {typeof s.fields.location_or_link === 'string' && s.fields.location_or_link
                      ? ` · ${s.fields.location_or_link}`
                      : ''}
                  </span>
                </button>
              ))
            )
          ) : null}

          {tab === 'tapes' ? (
            <>
              <div class="sp__acts">
                <label class={`sp__act${attaching ? ' sp__act--busy' : ''}`}>
                  {attaching ? 'Adding the tape…' : '+ Add a tape'}
                  <input
                    type="file"
                    accept="video/*"
                    class="sp__file"
                    data-testid="tape-file"
                    disabled={attaching}
                    onChange={(e) => void attachTape(e)}
                  />
                </label>
              </div>
              {attachRefusal ? (
                <p class="sp__craft-say" data-testid="tape-refusal">
                  {attachRefusal}
                </p>
              ) : null}

              {tapesFailure ? (
                <>
                  <div class="sp__say" data-testid="tapes-failure">
                    {tapesFailure.headline}
                    <br />
                    <span class="sp__say-soft">{tapesFailure.detail}</span>
                  </div>
                  {tapesFailure.retryable ? (
                    <button type="button" class="sp__retry" onClick={() => setFeedbackNonce((n) => n + 1)}>
                      Read it again
                    </button>
                  ) : null}
                </>
              ) : !tapesLoaded ? null : tapes.length === 0 ? (
                <div class="sp__empty">Nothing taped on this read yet.</div>
              ) : (
                <>
                  {playable ? (
                    <div class="st__player">
                      <RecordVideo tenantId={tenantId} recordId={playable.id} fieldName="original_file_ref" />
                    </div>
                  ) : null}
                  {/* B15 §4 — AFTER A TAPE, OFFER THE MOVE, NEVER MAKE IT. A
                      tape kept here may be a practice take, or one going to a
                      coach first ("Send for notes") — so a tape landing on a
                      still-Requested read never auto-moves the stage; this is
                      one tap away, not zero. */}
                  {record.lifecycle_state === 'requested' ? (
                    <div class="sp__acts">
                      <button
                        type="button"
                        class="sp__act"
                        data-testid="offer-submit"
                        onClick={() => void doMove('submitted', { submitted_at: new Date().toISOString() })}
                      >
                        Sent it? Mark as submitted
                      </button>
                    </div>
                  ) : null}
                  {tapes.map((t) => {
                    const hasFile =
                      typeof t.fields.original_file_ref === 'string' && t.fields.original_file_ref;
                    const sent = t.fields.take_sent;
                    const shot = t.fields.takes_shot;
                    return (
                      <button
                        type="button"
                        key={t.id}
                        class="sp__row"
                        data-testid="tape-row"
                        onClick={() => openTape(t.id)}
                      >
                        <span class="sp__row-top">
                          <span class="sp__row-name">
                            {/* The play glyph is A6's own mark for "this one
                                can actually be watched" — the same one the
                                tape log carries, so a row means the same
                                thing on both screens. */}
                            {hasFile ? (
                              <span class="st__has-video" aria-label="has a recording">
                                ▸
                              </span>
                            ) : null}
                            {typeof t.fields.label === 'string' && t.fields.label.trim()
                              ? t.fields.label
                              : typeof t.fields.kind === 'string'
                                ? humanizeValue(t.fields.kind)
                                : 'Tape'}
                          </span>
                          <span class="sp__row-when">{when(t.fields.recorded_at) ?? ''}</span>
                        </span>
                        <span class="sp__row-sub">
                          {[
                            typeof t.fields.kind === 'string' ? humanizeValue(t.fields.kind) : null,
                            typeof sent === 'number'
                              ? `take ${sent}${typeof shot === 'number' ? ` of ${shot}` : ''} sent`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              <SendForNotes
                tenantId={tenantId}
                auditionId={id}
                reviewers={reviewers}
                failure={reviewersFailure}
                plan={plan}
                onChanged={() => setFeedbackNonce((n) => n + 1)}
              />
            </>
          ) : null}

          {tab === 'sides' ? (
            !around.loaded ? null : trail.length === 0 ? (
              <div class="sp__empty">No pages issued for this part yet.</div>
            ) : (
              trail.map((row) => (
                <button
                  type="button"
                  key={row.id}
                  class="sp__row"
                  onClick={() => openRecordEditor({ mode: 'edit', typeKey: AA.SIDES, id: row.id })}
                >
                  <span class="sp__row-top">
                    <span class="sp__row-name">{sidesLabel(row)}</span>
                    <span class="sp__row-when">{when(row.fields.issued_at) ?? '—'}</span>
                  </span>
                  {typeof row.fields.changed_scenes === 'string' && row.fields.changed_scenes.trim() ? (
                    <span class="sp__row-sub">{row.fields.changed_scenes}</span>
                  ) : null}
                </button>
              ))
            )
          ) : null}

          {tab === 'notes' ? (
            <>
              {auditionNote ? (
                <p class="sp__row-sub" style={{ padding: '12px 2px' }}>
                  {auditionNote}
                </p>
              ) : null}

              {/* ---- WHAT THE COACHES SAID ---- */}
              {coachNotes.length > 0 ? (
                <>
                  <div class="sp__sec">
                    <span>What the coaches said</span>
                    <span>
                      {coachNotes.length} {coachNotes.length === 1 ? 'note' : 'notes'}
                    </span>
                  </div>

                  {/* ⚑ THE SUMMARY IS QUOTATION, NEVER OUR PROSE. Every line
                      below is a sentence a coach actually wrote, re-checked
                      server-side against the note it came from; the name under
                      each one is what makes that visible rather than claimed. */}
                  {craftOn && craft?.summary ? (
                    <>
                      <div class="sp__craft" data-testid="craft-summary">
                        {craft.summary.lines.map((line) => (
                          <p class="sp__craft-line" key={line.noteId + line.quote}>
                            &ldquo;{line.quote}&rdquo;
                            <span class="sp__craft-who">
                              {line.reviewerName ?? 'a reviewer'}
                              {line.reviewerRole && line.reviewerRole !== 'other'
                                ? ` · ${line.reviewerRole}`
                                : ''}
                            </span>
                          </p>
                        ))}
                      </div>
                      {/* THE STALE LABEL. A summary that predates a note says
                          so — showing it as current would be the quiet lie the
                          degrade rule exists to prevent. */}
                      {craft.summary.stale ? (
                        <p class="sp__craft-say" data-testid="craft-stale">
                          Drawn from {craft.summary.fromNoteCount} of {craft.summary.currentNoteCount} notes.
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {/* THE TEASER — one line, honest, in the slot the summary
                      would occupy. Not a degraded copy of the feature and not
                      a control that errors: the layer is simply not offered. */}
                  {!craftOn ? (
                    <p class="sp__craft-say" data-testid="craft-teaser">
                      On Working, your coaches&rsquo; notes get summarized here.
                    </p>
                  ) : null}
                  {craftOn && !craft?.summary && craft && craft.coachNoteCount < craft.minCoachNotes ? (
                    <p class="sp__craft-say" data-testid="craft-waiting">
                      A summary arrives once a second coach has written.
                    </p>
                  ) : null}

                  {coachNotes.map((r) => (
                    <div class="sp__row" key={r.id} data-testid="coach-note">
                      <span class="sp__row-top">
                        <span class="sp__row-name">
                          {r.reviewerName ?? 'A reviewer'}
                          {r.reviewerRole ? (
                            <span class="sp__row-role" data-testid="coach-note-role">
                              {REVIEWER_ROLE_LABEL[r.reviewerRole] ?? r.reviewerRole}
                            </span>
                          ) : null}
                        </span>
                        <span class="sp__row-when">{when(r.submittedAt) ?? ''}</span>
                      </span>
                      <CoachNoteBody tenantId={tenantId} review={r} />
                    </div>
                  ))}
                </>
              ) : null}

              {/* ---- YOUR NOTES, BESIDE THEIRS ---- */}
              {craftOn ? (
                <>
                  <div class="sp__sec">
                    <span>Your notes</span>
                    {selfNotes.length > 0 ? <span>{selfNotes.length}</span> : null}
                  </div>
                  {selfNotes.map((r) => (
                    <div class="sp__row" key={r.id} data-testid="self-note">
                      <span class="sp__row-top">
                        <span class="sp__row-name">{r.reviewerName ?? 'You'}</span>
                        <span class="sp__row-when">{when(r.submittedAt) ?? ''}</span>
                      </span>
                      <span class="sp__row-sub">{r.body ?? r.transcript}</span>
                    </div>
                  ))}
                  <textarea
                    class="sp__craft-self"
                    data-testid="self-note-draft"
                    value={draft}
                    onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
                  />
                  <div class="sp__acts">
                    <button
                      type="button"
                      class="sp__act"
                      disabled={saving || draft.trim().length === 0}
                      onClick={() => void saveSelfNote()}
                    >
                      {saving ? 'Saving…' : 'Keep this note'}
                    </button>
                  </div>
                  {noteRefusal ? (
                    <p class="sp__craft-say" data-testid="self-note-refusal">
                      {noteRefusal}
                    </p>
                  ) : null}
                </>
              ) : null}

              {craftSay ? (
                <p class="sp__craft-say" data-testid="craft-failure">
                  {craftSay}
                </p>
              ) : null}

              {/* A HEADING WITH NOTHING UNDER IT IS A DEFECT — this screen's
                  own rule. The empty line appears only when the tab really
                  holds nothing: no audition note, no reviews, and no composer
                  of the family's own. */}
              {!auditionNote && reviews.length === 0 && !craftOn ? (
                <div class="sp__empty">Nothing written down about this read.</div>
              ) : null}
            </>
          ) : null}

          {/* The outcome of a pass is required by the pack precisely so it is
              recorded rather than remembered — it is what the coach loop and
              Stats read. When there is one it belongs on the screen, not
              behind an edit form. */}
          {typeof record.fields.outcome === 'string' && record.fields.outcome.trim() ? (
            <>
              <div class="sp__sec">
                <span>Outcome</span>
              </div>
              <p class="sp__row-sub">{record.fields.outcome}</p>
            </>
          ) : null}
        </div>

        {/* ---- THE RAIL: everything, including zeros ---- */}
        <div class="sp__rail">
          {rail.map((p) => (
            <button
              type="button"
              key={p.label}
              class={`sp__pill${p.n === 0 && !p.always ? ' sp__pill--zero' : ''}${p.on ? ' sp__pill--on' : ''}`}
              disabled={(p.n === 0 && !p.always) || !p.go}
              onClick={() => p.go?.()}
            >
              {p.label} <span class="sp__pill-n">{p.n}</span>
            </button>
          ))}
          <button
            type="button"
            class="sp__pill"
            onClick={() =>
              openRecordEditor({
                mode: 'create',
                typeKey: AA.SESSION,
                prefillRels: { [LINK.SESSION_AUDITION]: [id] },
              })
            }
          >
            + Add a session
          </button>
        </div>
      </div>

      {failure ? (
        <p class="sp__say-soft" style={{ margin: '12px 2px' }}>
          {failure.headline} — {failure.detail}
        </p>
      ) : null}

      {/* Every declared field the screen did not already say, so nothing the
          household entered is invisible. Rendered from the schema, so a field
          added to the pack appears here without an edit to this file. */}
      {/* A HEADING WITH NOTHING UNDER IT IS A DEFECT, not a tidy empty state:
          it promises a section the record does not have. So the rest is
          computed first and the heading only appears if the rest exists.
          Rendered from the schema, so a field added to the pack shows up here
          without an edit to this file. */}
      {(() => {
        const rest = (schema?.fields ?? [])
          .filter((f) => !SAID_IN_THE_SPREAD.has(f.name))
          .map((f) => ({ field: f, text: formatFieldValue(f, record.fields[f.name]) }))
          .filter((x) => Boolean(x.text));
        if (rest.length === 0) return null;
        return (
          <>
            <div class="sp__sec" style={{ marginTop: '20px' }}>
              <span>Everything else recorded</span>
            </div>
            {rest.map(({ field, text }) => (
              <p class="sp__row-sub" key={field.name}>
                <span class="sp__row-when">{field.label} </span>
                {text}
              </p>
            ))}
          </>
        );
      })()}
    </div>
  );
}

/** Fields the regions above already rendered. Anything not in here falls
 * through to the tail list, so a field added to the pack is visible the day
 * it ships rather than the day someone remembers this screen. */
const SAID_IN_THE_SPREAD = new Set([
  'modality',
  'season',
  'episode',
  'submitted_at',
  'due_at',
  'outcome',
  'notes',
]);
